import React, { FC, useEffect, useCallback } from "react";
import { Box, Button, ThemeProvider } from "@mui/material";
import {
  DataGridPro,
  GridFooterContainer,
  GridPagination,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import themeInarix from "../../StylesMuiInarix";
import { useDispatch, useSelector } from "react-redux";
import {
  initialState,
  isViewerOnly,
  localeData,
  setColumns,
  setColumnOrder,
  setIsOpenFilters,
  setSideBarFiltersVisible,
} from "../../../redux/slices/dataTable/data";

import DataTableColumns from "./columnsAndActions/ColumnsAndActions";
import MenuOptions from "./MenuOptions/MenuOptions";
import { SwitchesStyles } from "./MenuOptions/options/columnSwitches/ColumnSwitchesStyles";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { dataTableActions } from "../../../redux/actions/dataTable/DataTableAsyncActions";
import {
  selectDataTableUnifiedState,
  selectExportDataState,
} from "../../../redux/selectors/unifiedSelectors";
import { setTableDataOptions } from "../../../redux/slices/UnifiedSlices/SharedDataReducer";
import { State } from "../../../redux/slices";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    setColumn: (columns: any) => dispatch(setColumns(columns)),
    setColumnOrder: (columnOrder: string[]) =>
      dispatch(setColumnOrder(columnOrder)),
    setSideBarFiltersVisible: (visible: boolean) => {
      dispatch(setSideBarFiltersVisible(visible));
      dispatch(setIsOpenFilters(initialState.isOpenFilters));
    },
    setNewPage: (
      tableOptions: {
        page: number;
        rowOptions: number;
        totalRows: number | null;
      },
      page: number,
      rowOptions: number
    ) => {
      dispatch(setTableDataOptions({ ...tableOptions, page, rowOptions }));
      dispatch(dataTableActions());
    },
  };
};

const DataTable: FC = () => {
  const [t] = useTranslation("global");
  const apiRef = useGridApiRef();
  const locale = useSelector(localeData);
  const viewerOnly = useSelector(isViewerOnly);

  const { columnsDataTable, actionsDataTable } = DataTableColumns();

  const {
    tableOptions,
    columnsVisibility,
    loading,
    filtersSideBarVisible,
    pinnedColumns,
    columnOrder,
    filters,
  } = useSelector(selectDataTableUnifiedState);

  const loadingFilters = filters.loadingFilters;
  const filtersSelected = filters.filtersSelectedLength;

  const { loadingExport } = useSelector(selectExportDataState);

  const applyColumnOrder = useCallback(() => {
    if (
      columnOrder &&
      columnOrder.length > 0 &&
      apiRef.current &&
      columnsDataTable.length > 0
    ) {
      // get the current order of columns in the table
      const currentColumns = apiRef.current.getAllColumns();
      const currentOrder = currentColumns.map((col) => col.field);

      // Compare the columns actual orders
      const isOrderDifferent =
        JSON.stringify(currentOrder) !== JSON.stringify(columnOrder);

      if (isOrderDifferent) {
        // Just order if is necessary
        const currentFieldsSet = new Set(currentOrder);
        const validOrder = columnOrder.filter((field) =>
          currentFieldsSet.has(field)
        );

        if (validOrder.length > 0) {
          // Reorder columns
          for (let i = 0; i < validOrder.length; i++) {
            const field = validOrder[i];
            const currentIndex = apiRef.current.getColumnIndex(field);
            if (currentIndex !== i) {
              apiRef.current.setColumnIndex(field, i);
            }
          }
        }
      }
    }
  }, [columnOrder, columnsDataTable, apiRef]);

  useEffect(() => {
    applyColumnOrder();
  }, [applyColumnOrder]);

  const loadings = loading || loadingExport || loadingFilters;

  const paginationModel = {
    pageSize: tableOptions.rowOptions,
    page: tableOptions.page,
  };
  const { setColumn, setColumnOrder, setNewPage, setSideBarFiltersVisible } =
    SetActions();

  const handlePageChange = (page: number, rowOption: number) => {
    setNewPage(tableOptions, page, rowOption);
  };

  const paginationToolbar = () => {
    return (
      <GridFooterContainer sx={{ alignItems: "baseline" }}>
        <div className="grid-foot-container">
          {tableOptions.page > 0 ? (
            <Button
              variant="contained"
              onClick={() => handlePageChange(0, tableOptions.rowOptions)}
              disabled={tableOptions.page < 1}
            >
              {t("Data.table.Restart_page")}
            </Button>
          ) : (
            ""
          )}
          <GridPagination />
        </div>
      </GridFooterContainer>
    );
  };

  return (
    <Box
      onClick={
        filtersSideBarVisible ? () => setSideBarFiltersVisible(false) : () => {}
      }
      sx={SwitchesStyles.DataTableBox}
    >
      <MenuOptions loadings={loadings} isViewerOnly={viewerOnly} />
      <ThemeProvider theme={themeInarix(locale)}>
        <DataGridPro
          loading={loading || columnsDataTable.length === 0}
          className="table-styles-data"
          columns={columnsDataTable ?? []}
          pinnedColumns={pinnedColumns}
          onColumnOrderChange={() => {
            // Solo capturar cambios después de un delay para evitar conflictos con applyColumnOrder
            setTimeout(() => {
              if (apiRef.current) {
                const allColumns = apiRef.current.getAllColumns();
                setColumn(allColumns);
                const columnOrderArray = allColumns.map((col) => col.field);
                setColumnOrder(columnOrderArray);
              }
            }, 200);
          }}
          onColumnWidthChange={() => setColumn(apiRef.current.getAllColumns())}
          rows={actionsDataTable}
          paginationMode="server"
          filterMode="server"
          columnVisibilityModel={columnsVisibility}
          paginationModel={paginationModel}
          slots={{ footer: paginationToolbar }}
          onPaginationModelChange={(newPage) => {
            handlePageChange(newPage.page, newPage.pageSize);
          }}
          rowCount={tableOptions.totalRows ?? 0}
          initialState={{
            pagination: {
              paginationModel: { pageSize: tableOptions.rowOptions },
            },
          }}
          localeText={{
            noRowsLabel:
              filtersSelected === 0
                ? t("Data.no_results_data")
                : t("Data.no_results_filters"),
          }}
          pagination
          rowHeight={70}
          pageSizeOptions={[100, 125, 150]}
          apiRef={apiRef}
        />
      </ThemeProvider>
    </Box>
  );
};

export default DataTable;
